// Recipe payload types and pure helpers shared by server and UI (spec §3.3).

export interface IngredientItem {
  qty: number | null;
  qty_max: number | null;
  unit: string | null;
  item: string;
  prep: string | null;
  note: string | null;
  optional: boolean;
  scalable: boolean;
  linked_card_id: string | null;
  raw: string;
}

export interface IngredientGroup {
  name: string | null;
  items: IngredientItem[];
}

export interface RecipeStep {
  n: number;
  text: string;
  timer_min: number | null;
  media_id: string | null;
}

export interface CookedEntry {
  date: string;
  note?: string | null;
  rating?: number | null;
}

export interface RecipeData {
  servings: { amount: number; unit: string } | null;
  times: { prep_min?: number | null; cook_min?: number | null; rest_min?: number | null; total_min?: number | null };
  difficulty: 'easy' | 'medium' | 'hard' | null;
  cuisine: string[];
  course: string[];
  diet: string[];
  season: string[];
  equipment: string[];
  ingredient_groups: IngredientGroup[];
  steps: RecipeStep[];
  nutrition: Record<string, unknown> | null;
  yield_note: string | null;
  cooked_log: CookedEntry[];
}

/** Canonical unit vocabulary; imperial units are accepted on input. */
export const UNITS = [
  'g', 'kg', 'ml', 'l', 'tsp', 'tbsp', 'cup', 'pinch', 'piece', 'clove', 'can', 'bunch', 'slice', 'sprig', 'to_taste',
  'oz', 'lb', 'fl_oz',
] as const;
export type Unit = (typeof UNITS)[number];
export const isKnownUnit = (u: string | null | undefined): boolean => !u || (UNITS as readonly string[]).includes(u);

const UNIT_ALIASES: Record<string, Unit> = {
  g: 'g', gr: 'g', gram: 'g', grams: 'g', gramme: 'g', grammes: 'g',
  kg: 'kg', kilo: 'kg', kilos: 'kg', kilogram: 'kg', kilograms: 'kg',
  ml: 'ml', millilitre: 'ml', milliliter: 'ml', millilitres: 'ml', milliliters: 'ml',
  cl: 'ml', dl: 'ml', // converted below
  l: 'l', litre: 'l', liter: 'l', litres: 'l', liters: 'l',
  tsp: 'tsp', teaspoon: 'tsp', teaspoons: 'tsp', tl: 'tsp', 't': 'tsp',
  tbsp: 'tbsp', tbs: 'tbsp', tbl: 'tbsp', tablespoon: 'tbsp', tablespoons: 'tbsp', el: 'tbsp', T: 'tbsp',
  cup: 'cup', cups: 'cup', c: 'cup',
  pinch: 'pinch', pinches: 'pinch', snuf: 'pinch', snufje: 'pinch',
  piece: 'piece', pieces: 'piece', pc: 'piece', pcs: 'piece',
  clove: 'clove', cloves: 'clove', teen: 'clove', teentjes: 'clove', teentje: 'clove',
  can: 'can', cans: 'can', tin: 'can', tins: 'can', blik: 'can',
  bunch: 'bunch', bunches: 'bunch', bos: 'bunch', bosje: 'bunch',
  slice: 'slice', slices: 'slice', plak: 'slice', plakjes: 'slice',
  sprig: 'sprig', sprigs: 'sprig', takje: 'sprig', takjes: 'sprig',
  oz: 'oz', ounce: 'oz', ounces: 'oz',
  lb: 'lb', lbs: 'lb', pound: 'lb', pounds: 'lb',
  'fl_oz': 'fl_oz', floz: 'fl_oz',
};

const UNICODE_FRACTIONS: Record<string, number> = {
  '½': 1 / 2, '⅓': 1 / 3, '⅔': 2 / 3, '¼': 1 / 4, '¾': 3 / 4, '⅕': 1 / 5, '⅖': 2 / 5, '⅗': 3 / 5, '⅘': 4 / 5,
  '⅙': 1 / 6, '⅚': 5 / 6, '⅛': 1 / 8, '⅜': 3 / 8, '⅝': 5 / 8, '⅞': 7 / 8,
};

const NON_SCALABLE = /\b(to taste|for serving|to serve|for garnish|bay lea(f|ves)|salt and pepper|as needed|naar smaak)\b/i;

/** Parses one number token: "2", "1.5", "1,5", "1/2", "1 1/2", "1½", "½". */
function parseNumber(s: string): number | null {
  s = s.trim();
  if (!s) return null;
  let m = s.match(/^(\d+)\s*([½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])$/);
  if (m) return Number(m[1]) + UNICODE_FRACTIONS[m[2]];
  if (UNICODE_FRACTIONS[s] !== undefined) return UNICODE_FRACTIONS[s];
  m = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (m) return Number(m[1]) + Number(m[2]) / Number(m[3]);
  m = s.match(/^(\d+)\/(\d+)$/);
  if (m) return Number(m[2]) === 0 ? null : Number(m[1]) / Number(m[2]);
  m = s.match(/^\d+([.,]\d+)?$/);
  if (m) return Number(s.replace(',', '.'));
  return null;
}

const NUM = String.raw`(?:\d+\s+\d+\/\d+|\d+\/\d+|\d+\s*[½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞]|[½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞]|\d+(?:[.,]\d+)?)`;
const QTY_RE = new RegExp(String.raw`^(${NUM})(?:\s*(?:-|–|—|to|tot)\s*(${NUM}))?`, 'i');

/**
 * Deterministic ingredient-line parser. Never throws; anything it cannot
 * structure stays in `item`, and `raw` always keeps the original line.
 */
export function parseIngredientLine(rawLine: string): IngredientItem {
  const raw = rawLine.trim().replace(/^[-*•·]\s*/, '');
  let rest = raw;
  let qty: number | null = null;
  let qty_max: number | null = null;
  let unit: string | null = null;
  let optional = false;
  let note: string | null = null;
  let prep: string | null = null;

  if (/^(a|an|one)\s+/i.test(rest) && /^(a|an|one)\s+(pinch|clove|can|tin|bunch|sprig|slice|handful|piece)\b/i.test(rest)) {
    qty = 1;
    rest = rest.replace(/^(a|an|one)\s+/i, '');
  }
  const q = rest.match(QTY_RE);
  if (q) {
    qty = parseNumber(q[1]);
    qty_max = q[2] ? parseNumber(q[2]) : null;
    rest = rest.slice(q[0].length).trim();
  }

  // Unit, possibly glued to the number ("200g") or followed by "of".
  const u = rest.match(/^(fl\.?\s*oz\.?|[a-zA-Z_]+\.?)(?=\s|$|\)|,|[-–—])/);
  if (u) {
    const token = u[1].replace(/\./g, '').replace(/\s+/g, '');
    const lower = token.toLowerCase();
    const canon = token === 'T' ? 'tbsp' : token === 't' ? 'tsp' : UNIT_ALIASES[lower];
    if (canon && (qty !== null || ['pinch', 'bunch', 'sprig'].includes(canon))) {
      unit = canon;
      if (lower === 'cl' && qty !== null) { qty *= 10; if (qty_max !== null) qty_max *= 10; }
      if (lower === 'dl' && qty !== null) { qty *= 100; if (qty_max !== null) qty_max *= 100; }
      if (canon === 'pinch' && qty === null) qty = 1;
      rest = rest.slice(u[0].length).trim().replace(/^of\s+/i, '');
      // Range with a unit on both sides: "750 ml–1 l stock"
      const r2 = rest.match(new RegExp(String.raw`^(?:-|–|—|to)\s*(${NUM})\s*([a-zA-Z_]+)\b\s*`, 'i'));
      if (r2 && qty_max === null && qty !== null) {
        const v = parseNumber(r2[1]);
        const u2 = UNIT_ALIASES[r2[2].toLowerCase()];
        if (v !== null && u2) {
          qty_max = convertBack(v, u2, unit);
          rest = rest.slice(r2[0].length);
        }
      }
    }
  }

  // "1 (400 g) can tomatoes" → note "400 g", unit can.
  const paren = rest.match(/^\(([^)]*)\)\s*/);
  if (paren) {
    note = paren[1].trim();
    rest = rest.slice(paren[0].length);
    const u2 = rest.match(/^([a-zA-Z]+)\s+/);
    if (u2 && UNIT_ALIASES[u2[1].toLowerCase()] && !unit) {
      unit = UNIT_ALIASES[u2[1].toLowerCase()];
      rest = rest.slice(u2[0].length);
    }
  }

  if (/\(optional\)|,\s*optional\b|\boptional\s*$/i.test(rest)) {
    optional = true;
    rest = rest.replace(/\s*\(optional\)|,\s*optional\b|\s*\boptional\s*$/i, '');
  }
  if (/\bto taste\b|\bnaar smaak\b/i.test(rest) && qty === null) unit = 'to_taste';

  const trailingParen = rest.match(/\s*\(([^)]*)\)\s*$/);
  if (trailingParen) {
    note = [note, trailingParen[1].trim()].filter(Boolean).join('; ');
    rest = rest.slice(0, trailingParen.index).trim();
  }
  const orMatch = rest.match(/,?\s+(or\s+.+)$/i);
  if (orMatch && !rest.includes(',')) {
    note = [note, orMatch[1]].filter(Boolean).join('; ');
    rest = rest.slice(0, orMatch.index).trim();
  }
  const comma = rest.indexOf(',');
  if (comma > 0) {
    prep = rest.slice(comma + 1).trim() || null;
    rest = rest.slice(0, comma).trim();
  }
  if (unit === 'to_taste') rest = rest.replace(/\s*\bto taste\b|\s*\bnaar smaak\b/i, '').trim();

  const item = rest.trim() || raw;
  const scalable = qty !== null && unit !== 'to_taste' && unit !== 'pinch' && !NON_SCALABLE.test(raw);
  return {
    qty,
    qty_max,
    unit,
    item,
    prep,
    note: note || null,
    optional,
    scalable,
    linked_card_id: null,
    raw,
  };
}

// ---------------------------------------------------------------- scaling & units

export type UnitMode = 'as_written' | 'metric' | 'us';

const TO_G: Record<string, number> = { g: 1, kg: 1000, oz: 28.3495, lb: 453.592 };
const TO_ML: Record<string, number> = { ml: 1, l: 1000, fl_oz: 29.5735, cup: 240, tbsp: 15, tsp: 5 };

export interface DisplayQty {
  qty: number | null;
  qty_max: number | null;
  unit: string | null;
}

function convertOne(v: number, unit: string, mode: UnitMode): { v: number; unit: string } {
  if (mode === 'as_written') return { v, unit };
  if (mode === 'metric') {
    if (unit in TO_G && unit !== 'g' && unit !== 'kg') return { v: v * TO_G[unit], unit: 'g' };
    if (unit === 'fl_oz' || unit === 'cup') return { v: v * TO_ML[unit], unit: 'ml' };
    if (unit === 'g' && v >= 1000) return { v: v / 1000, unit: 'kg' };
    if (unit === 'ml' && v >= 1000) return { v: v / 1000, unit: 'l' };
    return { v, unit };
  }
  // US customary
  if (unit === 'g' || unit === 'kg') {
    const oz = (v * TO_G[unit]) / TO_G.oz;
    return oz >= 16 ? { v: oz / 16, unit: 'lb' } : { v: oz, unit: 'oz' };
  }
  if (unit === 'ml' || unit === 'l') {
    const ml = v * TO_ML[unit];
    if (ml >= 60) return { v: ml / TO_ML.cup, unit: 'cup' };
    if (ml >= 15) return { v: ml / TO_ML.tbsp, unit: 'tbsp' };
    return { v: ml / TO_ML.tsp, unit: 'tsp' };
  }
  return { v, unit };
}

/** Scales and converts an ingredient quantity for display. Non-scalable items keep their quantity. */
export function scaleItem(item: Pick<IngredientItem, 'qty' | 'qty_max' | 'unit' | 'scalable'>, factor: number, mode: UnitMode): DisplayQty {
  if (item.qty === null) return { qty: null, qty_max: null, unit: item.unit };
  const f = item.scalable ? factor : 1;
  const unit = item.unit ?? '';
  const a = convertOne(item.qty * f, unit, mode);
  const b = item.qty_max !== null ? convertOne(item.qty_max * f, unit, mode) : null;
  // keep range in the same unit as the lower bound
  let max: number | null = null;
  if (b) max = b.unit === a.unit ? b.v : convertBack(item.qty_max! * f, unit, a.unit);
  return { qty: a.v, qty_max: max, unit: a.unit || null };
}

function convertBack(v: number, from: string, to: string): number {
  if (from in TO_G && to in TO_G) return (v * TO_G[from]) / TO_G[to];
  if (from in TO_ML && to in TO_ML) return (v * TO_ML[from]) / TO_ML[to];
  return v;
}

const FRACTION_UNITS = new Set(['tsp', 'tbsp', 'cup', 'oz', 'lb', 'fl_oz', 'piece', 'clove', 'can', 'bunch', 'slice', 'sprig', '']);
const NICE_FRACTIONS: [number, string][] = [
  [0, ''], [1 / 8, '⅛'], [1 / 4, '¼'], [1 / 3, '⅓'], [3 / 8, '⅜'], [1 / 2, '½'], [5 / 8, '⅝'], [2 / 3, '⅔'], [3 / 4, '¾'], [7 / 8, '⅞'], [1, ''],
];

/** Formats a quantity: fractions for spoon/cup/count units, rounded metric otherwise. */
export function formatQty(v: number, unit: string | null): string {
  if (!Number.isFinite(v)) return '';
  const u = unit ?? '';
  if (FRACTION_UNITS.has(u)) {
    let whole = Math.floor(v);
    const frac = v - whole;
    let best = NICE_FRACTIONS[0];
    // Eighths only for small amounts; bigger quantities snap to friendlier fractions.
    const candidates = whole >= 2 ? NICE_FRACTIONS.filter(([f]) => [0, 1 / 4, 1 / 3, 1 / 2, 2 / 3, 3 / 4, 1].includes(f)) : NICE_FRACTIONS;
    for (const cand of candidates) if (Math.abs(cand[0] - frac) < Math.abs(best[0] - frac)) best = cand;
    if (best[0] === 1) { whole += 1; best = NICE_FRACTIONS[0]; }
    if (whole === 0 && best[1] === '') return v > 0 ? '⅛' : '0';
    return `${whole || ''}${best[1]}` || '0';
  }
  if (u === 'kg' || u === 'l') return trimNum(Math.round(v * 100) / 100);
  if (v < 10) return trimNum(Math.round(v * 10) / 10);
  if (v < 100) return String(Math.round(v));
  if (v < 1000) return String(Math.round(v / 5) * 5);
  return String(Math.round(v / 10) * 10);
}

function trimNum(n: number): string {
  return String(n).replace(/\.0+$/, '');
}

const UNIT_LABEL: Record<string, string> = { fl_oz: 'fl oz', to_taste: 'to taste', piece: '' };

export function unitLabel(unit: string | null, qty: number | null): string {
  if (!unit) return '';
  if (unit in UNIT_LABEL) return UNIT_LABEL[unit];
  const plural = qty !== null && qty > 1 && ['cup', 'clove', 'can', 'bunch', 'slice', 'sprig', 'pinch'].includes(unit);
  return plural ? (unit === 'bunch' || unit === 'pinch' ? `${unit}es` : `${unit}s`) : unit;
}

/** "2–3 tbsp olive oil, chopped (or butter)" */
export function formatIngredient(item: IngredientItem, factor = 1, mode: UnitMode = 'as_written'): string {
  const d = scaleItem(item, factor, mode);
  const parts: string[] = [];
  if (d.qty !== null) {
    let q = formatQty(d.qty, d.unit);
    if (d.qty_max !== null) q += `–${formatQty(d.qty_max, d.unit)}`;
    parts.push(q);
  }
  const ul = unitLabel(d.unit, d.qty_max ?? d.qty);
  if (ul && d.unit !== 'to_taste') parts.push(ul);
  parts.push(item.item);
  let s = parts.join(' ');
  if (d.unit === 'to_taste') s += ', to taste';
  if (item.prep) s += `, ${item.prep}`;
  if (item.note) s += ` (${item.note})`;
  if (item.optional) s += ' (optional)';
  return s;
}

/** Turns an ISO 8601 duration ("PT1H30M") into minutes. */
export function isoDurationToMinutes(s: unknown): number | null {
  if (typeof s !== 'string') return null;
  const m = s.match(/^P(?:(\d+)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i);
  if (!m) return null;
  const mins = Number(m[1] ?? 0) * 1440 + Number(m[2] ?? 0) * 60 + Number(m[3] ?? 0) + Number(m[4] ?? 0) / 60;
  return Math.round(mins) || null;
}

/** Finds "10 minutes", "1 hour", "1½ hours" in step text to suggest a timer. */
export function detectTimerMinutes(text: string): number | null {
  const m = text.match(new RegExp(String.raw`(${NUM})(?:\s*(?:-|–|to)\s*(${NUM}))?\s*(hours?|hrs?|minutes?|mins?|min)\b`, 'i'));
  if (!m) return null;
  const v = parseNumber(m[2] ?? m[1]);
  if (v === null) return null;
  return /^h/i.test(m[3]) ? Math.round(v * 60) : Math.round(v);
}

export function emptyRecipe(): RecipeData {
  return {
    servings: { amount: 4, unit: 'servings' },
    times: { prep_min: null, cook_min: null, rest_min: null, total_min: null },
    difficulty: null,
    cuisine: [], course: [], diet: [], season: [], equipment: [],
    ingredient_groups: [{ name: null, items: [] }],
    steps: [],
    nutrition: null,
    yield_note: null,
    cooked_log: [],
  };
}

export interface ParsedRecipeText {
  title: string | null;
  servings: number | null;
  ingredient_groups: IngredientGroup[];
  steps: RecipeStep[];
  notes: string;
}

const ING_HEAD = /^(ingredients?|ingrediënten|you(?:'ll)? need|what you need)\s*:?$/i;
const STEP_HEAD = /^(method|instructions?|directions?|steps?|preparation|bereiding|werkwijze)\s*:?$/i;
const NOTE_HEAD = /^(notes?|tips?|variations?|opmerkingen)\s*:?$/i;

/** Deterministic "paste text" parser (spec §4.5): splits ingredients/steps by headings and line patterns. */
export function parseRecipeText(text: string): ParsedRecipeText {
  const lines = text.replace(/\r\n?/g, '\n').split('\n').map((l) => l.trim());
  let section: 'pre' | 'ing' | 'steps' | 'notes' = 'pre';
  let title: string | null = null;
  let servings: number | null = null;
  const groups: IngredientGroup[] = [];
  const stepTexts: string[] = [];
  const notes: string[] = [];
  const pre: string[] = [];
  let current: IngredientGroup | null = null;

  for (const line of lines) {
    if (!line) continue;
    const sv = line.match(/^(?:serves|servings|yield|makes|voor)\s*:?\s*(\d+)/i);
    if (sv) { servings = Number(sv[1]); continue; }
    if (ING_HEAD.test(line)) { section = 'ing'; continue; }
    if (STEP_HEAD.test(line)) { section = 'steps'; continue; }
    if (NOTE_HEAD.test(line)) { section = 'notes'; continue; }
    if (section === 'pre') { if (!title) title = line.replace(/^#+\s*/, ''); else pre.push(line); continue; }
    if (section === 'ing') {
      const isGroupHead = (/:$/.test(line) && !QTY_RE.test(line)) || /^for the\b/i.test(line) || /^#+\s/.test(line);
      if (isGroupHead) {
        current = { name: line.replace(/^#+\s*/, '').replace(/:$/, '').trim(), items: [] };
        groups.push(current);
        continue;
      }
      if (!current) { current = { name: null, items: [] }; groups.push(current); }
      current.items.push(parseIngredientLine(line));
      continue;
    }
    if (section === 'steps') {
      const numbered = line.match(/^(?:step\s*)?(\d+)[.):]\s*(.*)$/i);
      if (numbered) stepTexts.push(numbered[2]);
      else if (stepTexts.length && /^[a-z]/.test(line)) stepTexts[stepTexts.length - 1] += ` ${line}`;
      else stepTexts.push(line.replace(/^[-*•]\s*/, ''));
      continue;
    }
    notes.push(line);
  }

  // No headings at all: treat quantity-led lines as ingredients, the rest as steps.
  if (!groups.length && !stepTexts.length && pre.length) {
    const g: IngredientGroup = { name: null, items: [] };
    for (const l of pre) {
      if (QTY_RE.test(l) && l.length < 80) g.items.push(parseIngredientLine(l));
      else stepTexts.push(l.replace(/^\d+[.)]\s*/, ''));
    }
    if (g.items.length) groups.push(g);
  } else if (pre.length) {
    notes.unshift(...pre);
  }

  return {
    title,
    servings,
    ingredient_groups: groups.length ? groups : [{ name: null, items: [] }],
    steps: stepTexts.filter(Boolean).map((t, i) => ({ n: i + 1, text: t, timer_min: detectTimerMinutes(t), media_id: null })),
    notes: notes.join('\n\n'),
  };
}

// ------------------------------------------------------------------ nutrition

/** Nutrients in UK label order: key in `data.nutrition`, label, unit. */
export const NUTRIENTS = [
  ['energy_kcal', 'Energy', 'kcal'],
  ['fat_g', 'Fat', 'g'],
  ['saturated_fat_g', 'of which saturates', 'g'],
  ['carbohydrates_g', 'Carbohydrate', 'g'],
  ['sugars_g', 'of which sugars', 'g'],
  ['fibre_g', 'Fibre', 'g'],
  ['protein_g', 'Protein', 'g'],
  ['salt_g', 'Salt', 'g'],
] as const;
export type NutrientKey = (typeof NUTRIENTS)[number][0];

export interface Nutrition {
  per_100g: Partial<Record<NutrientKey, number>>;
  whole_recipe: Partial<Record<NutrientKey, number>>;
  estimated_baked_weight_g: number | null;
  note: string | null;
}

const KEY_ALIASES: Record<string, NutrientKey> = {
  energy_kcal: 'energy_kcal', kcal: 'energy_kcal', calories: 'energy_kcal', calories_kcal: 'energy_kcal',
  fat_g: 'fat_g', saturated_fat_g: 'saturated_fat_g', saturates_g: 'saturated_fat_g',
  carbohydrates_g: 'carbohydrates_g', carbohydrate_g: 'carbohydrates_g', carbs_g: 'carbohydrates_g',
  sugars_g: 'sugars_g', sugar_g: 'sugars_g', fibre_g: 'fibre_g', fiber_g: 'fibre_g',
  protein_g: 'protein_g', salt_g: 'salt_g',
};

/** Nutrient key for a table row label such as "Carbohydrates (g)"; kJ rows are skipped (kcal is shown). */
function keyForLabel(label: string): NutrientKey | null {
  const l = label.toLowerCase();
  if (/\bkj\b/.test(l)) return null;
  if (/energy|calorie|kcal/.test(l)) return 'energy_kcal';
  if (/saturat/.test(l)) return 'saturated_fat_g';
  if (/sugar/.test(l)) return 'sugars_g';
  if (/carbohydrate|carbs/.test(l)) return 'carbohydrates_g';
  if (/fib(re|er)/.test(l)) return 'fibre_g';
  if (/protein/.test(l)) return 'protein_g';
  if (/salt/.test(l)) return 'salt_g';
  if (/\bfat\b/.test(l)) return 'fat_g';
  return null;
}

function numbers(raw: unknown): Partial<Record<NutrientKey, number>> {
  const out: Partial<Record<NutrientKey, number>> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [k, v] of Object.entries(raw)) {
    const key = KEY_ALIASES[k.toLowerCase()];
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
    if (key && Number.isFinite(n) && n >= 0) out[key] = n;
  }
  return out;
}

const NUTRITION_HEADING = /^##\s+[^\n]*nutrition[^\n]*$/im;

/** The "## … nutrition" section of a recipe's notes: its heading through to the next "## " heading. */
function nutritionSection(md: string): { start: number; end: number } | null {
  const m = NUTRITION_HEADING.exec(md);
  if (!m) return null;
  const after = m.index + m[0].length;
  const next = md.slice(after).search(/^##\s/m);
  return { start: m.index, end: next < 0 ? md.length : after + next };
}

/** Reads a nutrition table ("| Nutrient | Per 100 g | Entire recipe |") from the notes' nutrition section. */
function nutritionFromMarkdown(md: string): Nutrition | null {
  const sec = nutritionSection(md);
  if (!sec) return null;
  const text = md.slice(sec.start, sec.end);
  const rows = text.split('\n').filter((l) => l.trim().startsWith('|')).map((l) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim()));
  if (rows.length < 3) return null;
  const head = rows[0].map((h) => h.toLowerCase());
  const per100 = head.findIndex((h) => /100\s*g/.test(h));
  const whole = head.findIndex((h, i) => i > 0 && /recipe|whole|entire|total/.test(h));
  const n: Nutrition = { per_100g: {}, whole_recipe: {}, estimated_baked_weight_g: null, note: null };
  for (const r of rows.slice(1)) {
    const key = keyForLabel(r[0] ?? '');
    if (!key) continue;
    const val = (i: number) => (i > 0 ? Number((r[i] ?? '').replace(/[^\d.]/g, '')) : NaN);
    if (Number.isFinite(val(per100)) && r[per100]) n.per_100g[key] = val(per100);
    if (Number.isFinite(val(whole)) && r[whole]) n.whole_recipe[key] = val(whole);
  }
  const w = /baked weight[^\d]{0,20}([\d.]+)\s*g/i.exec(text);
  if (w) n.estimated_baked_weight_g = Number(w[1]);
  return Object.keys(n.per_100g).length || Object.keys(n.whole_recipe).length ? n : null;
}

/**
 * A recipe's nutrition from `data.nutrition` ({ per_100g, whole_recipe, estimated_baked_weight_g, note }),
 * or, for recipes that only have it in their notes, from the "## Estimated nutrition" table there.
 */
export function readNutrition(raw: unknown, notesMd?: string | null): Nutrition | null {
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    const n: Nutrition = {
      per_100g: numbers(r.per_100g),
      whole_recipe: numbers(r.whole_recipe ?? r.per_recipe ?? r.total),
      estimated_baked_weight_g: typeof r.estimated_baked_weight_g === 'number' && r.estimated_baked_weight_g > 0 ? r.estimated_baked_weight_g : null,
      note: typeof r.note === 'string' && r.note.trim() ? r.note.trim() : null,
    };
    if (Object.keys(n.per_100g).length || Object.keys(n.whole_recipe).length) return n;
  }
  return notesMd ? nutritionFromMarkdown(notesMd) : null;
}

/** The notes without their "## … nutrition" section (shown in its own section instead). */
export function withoutNutritionSection(md: string): string {
  const sec = nutritionSection(md);
  return sec ? (md.slice(0, sec.start) + md.slice(sec.end)).trim() : md;
}
